import { useEffect, useRef, useState, useCallback } from "react";
import {
    getPendingIotConnection,
    removePendingIotConnection,
    setPendingIotConnection,
} from "../utils/pendingIotConnectionStorage";

const POLL_INTERVAL = 3000;
const TIMEOUT_MS = 3 * 60 * 1000;

interface UseIoTConnectionPollingParams {
    assetLocationId: string;
    hasIotConnection: boolean;
    startPolling: (interval: number) => void;
    stopPolling: () => void;
}

export function useIoTConnectionPolling({
    assetLocationId,
    hasIotConnection,
    startPolling,
    stopPolling,
}: UseIoTConnectionPollingParams): { isPending: boolean; markAsPending: () => void } {
    const [isPending, setIsPending] = useState<boolean>(() => {
        const pending = getPendingIotConnection(assetLocationId);
        if (!pending) return false;
        if (Date.now() - pending.timestamp >= TIMEOUT_MS) {
            removePendingIotConnection(assetLocationId);
            return false;
        }
        return true;
    });

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearPending = useCallback(() => {
        stopPolling();
        removePendingIotConnection(assetLocationId);
        setIsPending(false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [assetLocationId, stopPolling]);

    useEffect(() => {
        if (!isPending) return;

        const pending = getPendingIotConnection(assetLocationId);
        if (!pending) {
            setIsPending(false);
            return;
        }

        const elapsed = Date.now() - pending.timestamp;
        if (elapsed >= TIMEOUT_MS) {
            clearPending();
            return;
        }

        startPolling(POLL_INTERVAL);

        const remaining = TIMEOUT_MS - elapsed;
        timeoutRef.current = setTimeout(() => {
            clearPending();
        }, remaining);

        return () => {
            stopPolling();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isPending, assetLocationId, startPolling, stopPolling, clearPending]);

    useEffect(() => {
        if (hasIotConnection && isPending) {
            clearPending();
        }
    }, [hasIotConnection, isPending, clearPending]);

    const markAsPending = useCallback(() => {
        setPendingIotConnection(assetLocationId);
        setIsPending(true);
    }, [assetLocationId]);

    return { isPending, markAsPending };
}
